/**
 * Who carries one pattern — the students behind a row in the pattern pivot.
 *
 * Fetches its own detail, the same way `UserStickingPatternsPanel` does for the user
 * pivot. That matters for more than symmetry: the table owns which row is
 * expanded, so a query keyed on the page's own "open" state would have to be
 * written during the table's render to stay in step. Letting the panel fetch
 * for the id it is handed keeps expansion state in one place.
 *
 * See docs/FeatureDesigns/diagnosis/sticking-patterns-surfaces.md.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchWithAdmin } from '../../api/fetchHelper';
import type { PatternDetailResponse } from './types';
import { formatWhen, trendLabel } from './format';
import './StickingPatterns.css';

export function StickingPatternCarriersPanel({ patternType, patternKey, onFilterUser }: {
  patternType: string;
  patternKey: string;
  /** Scope this page to one student. Preferred over navigating to their
   *  profile: the question a carriers list raises is "what ELSE does this
   *  student carry", which is this page's own By-user view. */
  onFilterUser?: (user: { userId: string; email: string; name?: string }) => void;
}) {

  const q = useQuery({
    queryKey: ['sticking-pattern-detail', patternType, patternKey],
    queryFn: () => fetchWithAdmin<PatternDetailResponse>(
      `/console/sticking-patterns/${encodeURIComponent(patternType)}/${encodeURIComponent(patternKey)}`,
    ),
    enabled: !!patternType && !!patternKey,
  });

  if (q.isLoading) return <p className="lp-muted">Loading carriers…</p>;
  if (q.isError) return <p className="lp-error">Failed: {(q.error as Error).message}</p>;

  const holders = q.data?.holders ?? [];
  if (holders.length === 0) return <p className="lp-empty">No students carry this pattern.</p>;

  return (
    <table className="dt dt--compact lp-carriers">
      <thead>
        <tr>
          <th className="lp-col-student">Student</th>
          <th className="lp-col-num">Times</th>
          <th className="lp-col-trend">Trend</th>
          <th>Last cause</th>
          <th className="lp-col-when">Last seen</th>
          <th className="lp-col-action" />
        </tr>
      </thead>
      <tbody>
        {holders.map(h => (
          <tr key={h.userId}>
            <td>
              <div className="lp-namecol">
                <span className="lp-name">{h.userName ?? '—'}</span>
                <span className="lp-key">{h.userEmail ?? h.userId}</span>
              </div>
            </td>
            <td className="lp-col-num">{h.recurrenceCount}</td>
            <td className="lp-dim lp-col-trend">{trendLabel(h.trend)}</td>
            <td className="lp-cause-cell">{h.lastCause ?? '—'}</td>
            <td className="lp-dim lp-col-when">{h.lastSeenAt ? formatWhen(h.lastSeenAt) : '—'}</td>
            <td className="lp-col-action">
              <button
                type="button"
                className="btn-link"
                onClick={() => onFilterUser?.({
                  userId: h.userId,
                  email: h.userEmail ?? h.userId,
                  name: h.userName ?? undefined,
                })}
              >
                Filter →
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
